import { getJWTToken } from '../../utils/auth';
import type { WidaStudent } from '../Upload/widaHistory';
import type { ReclassCriteria } from '../Reclassification/reclassCriteria';
import {
  DOMAIN_LABELS,
  accessLevel,
  currentLevel,
  criterionType,
  domainMinimumFor,
  domainScores,
  domainTrend,
  isLtel,
  levelChange,
  levelLabel,
  scoredAdministrations,
  type DomainKey,
} from './derive';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type FocusByDomainInputs = {
  domainName: string;
  domainProficiencyLevel: string;
  compositeProficiencyLevel: string;
  gradeBand: string;
  yearsInElBucket: string;
  domainPacingSignal: string;
  isLtelStudent: boolean;
  domainProficiencyScore: number | null;
  compositeProficiencyScore: number | null;
  domainYearOverYearChange: number | null;
  compositeYearOverYearChange: number | null;
  scoredAdministrationsCount: number;
  otherDomainScores: Record<string, number>;
  criterionType: 'exit' | 'review';
};

export type FocusByDomainContent = { recommendation: string; accommodations: string[] };

export function gradeBand(grade: string): string {
  const g = grade.trim().toUpperCase();
  if (g === 'K' || g === 'KG' || g === 'PK' || g === '0') return 'Grades K-2';
  const n = parseInt(g.replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(n)) return '';
  if (n <= 2) return 'Grades K-2';
  if (n <= 5) return 'Grades 3-5';
  if (n <= 8) return 'Grades 6-8';
  return 'Grades 9-12';
}

export function yearsBucket(years: number | null): string {
  if (years == null) return '';
  if (years < 2) return 'Under 2 years';
  if (years <= 4) return '2-4 years';
  return '5 or more years';
}

function growthDirection(student: WidaStudent, domain: DomainKey): string {
  switch (domainTrend(student, domain).category) {
    case 'rising':
      return 'growing';
    case 'flat':
      return 'flat';
    case 'falling':
      return 'declining';
    default:
      return 'pending';
  }
}

function pacingSignal(
  student: WidaStudent,
  domain: DomainKey,
  level: number,
  criteria: ReclassCriteria,
): string {
  const min = domainMinimumFor(criteria, domain);
  if (min == null) return growthDirection(student, domain);
  if (level >= min) return 'minimum met';
  if (isLtel(student)) return growthDirection(student, domain);
  return 'below minimum';
}

export function deriveFocusByDomainInputs(
  student: WidaStudent,
  domain: DomainKey,
  criteria: ReclassCriteria,
): FocusByDomainInputs | null {
  const domainLevel = currentLevel(student, domain);
  if (domainLevel == null) return null;
  const composite = currentLevel(student, 'composite');
  return {
    domainName: DOMAIN_LABELS[domain],
    domainProficiencyLevel: levelLabel(domainLevel),
    compositeProficiencyLevel: composite != null ? levelLabel(composite) : '',
    gradeBand: gradeBand(student.latestGrade ?? ''),
    yearsInElBucket: yearsBucket(student.yearsInEl),
    domainPacingSignal: pacingSignal(student, domain, domainLevel, criteria),
    isLtelStudent: isLtel(student),
    domainProficiencyScore: accessLevel(domainLevel),
    compositeProficiencyScore: accessLevel(composite),
    domainYearOverYearChange: levelChange(student, domain),
    compositeYearOverYearChange: levelChange(student, 'composite'),
    scoredAdministrationsCount: scoredAdministrations(student, domain),
    otherDomainScores: domainScores(student, domain),
    criterionType: criterionType(criteria),
  };
}

export async function fetchFocusByDomain(
  studentId: string,
  inputs: FocusByDomainInputs,
): Promise<FocusByDomainContent> {
  const token = await getJWTToken();
  const response = await fetch(
    `${apiUrl}/students/${encodeURIComponent(studentId)}/focus-by-domain`,
    {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(inputs),
    },
  );
  if (!response.ok) {
    throw new Error(`focus-by-domain fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as { recommendation?: string; accommodations?: string[] };
  return {
    recommendation: typeof data.recommendation === 'string' ? data.recommendation : '',
    accommodations: Array.isArray(data.accommodations) ? data.accommodations : [],
  };
}
